// src/components/music/AudioPlayerProvider.jsx

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MiniPlayer } from './parts/MiniPlayer';
import { REPEAT_MODES } from './constants/repeatModes';
import { shuffleArray } from './utils/audioUtils';

const PREVIEW_DURATION = 15;

const getTrackKey = (track) => track?.id || track?.key || track?.url || '';

const AudioPlayerContext = createContext(null);

export const useAudioPlayerContext = () => {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    throw new Error('useAudioPlayerContext must be used within an AudioPlayerProvider');
  }
  return ctx;
};

export function AudioPlayerProvider({ currentUser, children }) {
  const audioRef = useRef(null);
  const lastTrackRef = useRef(null);

  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.73);
  const [isMuted, setIsMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [compression, setCompression] = useState(-24);
  const [eqValues, setEqValuesState] = useState({ low: 0, mid: 0, high: 0 });
  const [isMiniPlayerVisible, setIsMiniPlayerVisible] = useState(false);

  const [playQueue, setPlayQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShuffling, setIsShuffling] = useState(false);
  const [repeatMode, setRepeatMode] = useState(REPEAT_MODES.OFF);

  const setEqValues = useCallback((band, value) => {
    setEqValuesState(prev => ({ ...prev, [band]: value }));
  }, []);

  const onCompressionChange = useCallback((val) => {
    setCompression(val);
  }, []);

  const resetAudioControls = useCallback(() => {
    setEqValues('low', 0);
    setEqValues('mid', 0);
    setEqValues('high', 0);
    onCompressionChange(-24);
    setSpeed(1);
    setVolume(1);
    setIsMuted(false);
  }, [setEqValues, onCompressionChange]);

  // Sync native audio volume/speed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
      audioRef.current.playbackRate = speed;
    }
  }, [volume, isMuted, speed]);

  // Load the selected track and drive play/pause
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (lastTrackRef.current !== currentTrack) {
      lastTrackRef.current = currentTrack;
      audio.src = currentTrack.url;
      audio.currentTime = 0;
    }
    if (isPlaying) {
      audio.play().catch((err) => console.warn('Playback failed:', err));
    } else {
      audio.pause();
    }
  }, [isPlaying, currentTrack]);

  const playNext = useCallback(() => {
    if (!playQueue.length) return;
    if (repeatMode === REPEAT_MODES.ONE && currentTrack) {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }
      return;
    }
    let nextIndex = currentIndex + 1;
    if (nextIndex >= playQueue.length) {
      if (repeatMode === REPEAT_MODES.ALL) {
        nextIndex = 0;
      } else {
        setIsPlaying(false);
        return;
      }
    }
    const nextTrack = playQueue[nextIndex];
    if (nextTrack) {
      setCurrentTrack(nextTrack);
      setCurrentIndex(nextIndex);
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [playQueue, repeatMode, currentIndex, currentTrack]);

  // Native audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDur = () => setDuration(audio.duration);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDur);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDur);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleEnded = () => playNext();
    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [playNext]);

  // Stop preview after 15s for non-logged users
  useEffect(() => {
    if (currentUser || !isPlaying) return;
    if (audioRef.current && audioRef.current.currentTime >= PREVIEW_DURATION) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [currentTime, currentUser, isPlaying]);

  const playPrev = useCallback(() => {
    const audio = audioRef.current;
    if (!playQueue.length) return;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      if (repeatMode === REPEAT_MODES.ALL) {
        prevIndex = playQueue.length - 1;
      } else {
        return;
      }
    }
    const prevTrack = playQueue[prevIndex];
    if (prevTrack) {
      setCurrentTrack(prevTrack);
      setCurrentIndex(prevIndex);
      setIsPlaying(true);
    }
  }, [playQueue, repeatMode, currentIndex]);

  const handlePlayPause = useCallback(
    (track, queueTracks) => {
      if (!track) return;
      if (currentTrack && getTrackKey(currentTrack) === getTrackKey(track)) {
        setIsPlaying(prev => !prev);
        setIsMiniPlayerVisible(true);
        return;
      }
      const tracks = queueTracks && queueTracks.length ? queueTracks : [track];
      const idx = tracks.findIndex(t => getTrackKey(t) === getTrackKey(track));
      setPlayQueue(tracks);
      setCurrentIndex(idx >= 0 ? idx : 0);
      setCurrentTrack(track);
      setIsPlaying(true);
      setIsMiniPlayerVisible(true);
    },
    [currentTrack]
  );

  const togglePlayPause = useCallback(() => {
    if (!currentTrack) return;
    setIsPlaying(prev => !prev);
  }, [currentTrack]);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const seekTo = useCallback(
    (time) => {
      const audio = audioRef.current;
      if (!audio) return;
      const next = Math.max(0, Math.min(time, duration || time));
      audio.currentTime = next;
      setCurrentTime(next);
    },
    [duration]
  );

  const handleProgressClick = useCallback(
    (e) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = x / rect.width;
      seekTo(percent * duration);
    },
    [duration, seekTo]
  );

  const toggleShuffle = useCallback(() => {
    setIsShuffling(prev => {
      const next = !prev;
      if (next && playQueue.length > 1) {
        const current = playQueue[currentIndex];
        const rest = playQueue.filter(t => getTrackKey(t) !== getTrackKey(current));
        setPlayQueue([current, ...shuffleArray(rest)]);
        setCurrentIndex(0);
      }
      return next;
    });
  }, [playQueue, currentIndex]);

  const toggleRepeat = useCallback(() => {
    setRepeatMode(prev => {
      switch (prev) {
        case REPEAT_MODES.OFF:
          return REPEAT_MODES.ALL;
        case REPEAT_MODES.ALL:
          return REPEAT_MODES.ONE;
        default:
          return REPEAT_MODES.OFF;
      }
    });
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    lastTrackRef.current = null;
    setCurrentTrack(null);
    setIsPlaying(false);
    setPlayQueue([]);
    setCurrentIndex(0);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const value = {
    audioRef,
    currentTrack,
    currentTime,
    duration,
    volume,
    setVolume,
    isMuted,
    setIsMuted,
    speed,
    setSpeed,
    eqValues,
    setEqValues,
    compression,
    onCompressionChange,
    resetAudioControls,
    isPlaying,
    togglePlayPause,
    pause,
    handlePlayPause,
    playNext,
    playPrev,
    seekTo,
    handleProgressClick,
    playQueue,
    currentIndex,
    isShuffling,
    toggleShuffle,
    repeatMode,
    toggleRepeat,
    isMiniPlayerVisible,
    setIsMiniPlayerVisible,
    stop,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="auto" crossOrigin="anonymous" />
      <MiniPlayer
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        isVisible={isMiniPlayerVisible}
        currentTime={currentTime}
        duration={duration}
        isShuffling={isShuffling}
        repeatMode={repeatMode}
        currentIndex={currentIndex}
        playQueue={playQueue}
        onPlayPause={togglePlayPause}
        onPrev={playPrev}
        onNext={playNext}
        onShuffle={toggleShuffle}
        onRepeat={toggleRepeat}
        onProgressClick={handleProgressClick}
        onHide={() => setIsMiniPlayerVisible(false)}
        onShow={() => setIsMiniPlayerVisible(true)}
        currentUser={currentUser}
        volume={volume}
        onVolumeChange={setVolume}
        isMuted={isMuted}
        onMuteToggle={() => setIsMuted(v => !v)}
        speed={speed}
        onSpeedChange={setSpeed}
        compression={compression}
        onCompressionChange={onCompressionChange}
        eqValues={eqValues}
        onEqChange={setEqValues}
        onResetAudioControls={resetAudioControls}
      />
    </AudioPlayerContext.Provider>
  );
}
